import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useActiveRealmAPIContext } from '@interxfi/api-client/realm-context';
import { PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { RequiresAccess } from '@interxfi/design-system/access-context';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import type { CustomTableHeaderProps } from '@interxfi/design-system/components/table/custom-header';
import PlusIcon from '@interxfi/design-system/icons/react/icon-plus';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../components/router/router-tabbed-navigation';
import { isAgentKeysAvailable } from '../../../../../features/agent-auth/agent-key-routing';
import { AgentKeysTable } from '../../../../../features/agent-auth/agent-keys-table';
import { CreateAgentKeyDrawer } from '../../../../../features/agent-auth/create-agent-key-drawer';
import { API_KEY_BASE_PATH } from '../../../../../features/api-auth/api-key-routing';
import { DevelopersTabRail } from '../../../../../features/developer/developers-tab-rail';
import { getPageHead } from '../../../../../util/routing/meta';
import { useRollupViewContext } from '../../../../../util/view-context';

export const Route = createFileRoute('/_app/developer/agent-key/_agent-key/')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.Developers,
      label: 'Agent Keys',
      order: 1,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.Developers>,
  head: () => getPageHead('Agent Keys'),
  component: AgentKeyList,
});

function TableHeader({ filter }: CustomTableHeaderProps) {
  return (
    <DevelopersTabRail
      actions={
        <HorizontalStack align="end" blockAlign="center" gap={2} wrap="nowrap">
          <RequiresAccess write>
            <CreateAgentKeyDrawer trigger={<Button affixIcon={<PlusIcon aria-hidden />} aria-label="New Agent Key" size="small" />} />
          </RequiresAccess>
          {filter}
        </HorizontalStack>
      }
    />
  );
}

function AgentKeyList() {
  const viewContext = useRollupViewContext();
  const realmContext = useActiveRealmAPIContext();

  if (!isAgentKeysAvailable(realmContext)) {
    return <Navigate replace to={API_KEY_BASE_PATH} />;
  }

  return (
    <PageLayout>
      <PageLayout.Section noBorder>
        <AgentKeysTable HeaderActions={TableHeader} view={viewContext} />
      </PageLayout.Section>
    </PageLayout>
  );
}
